import React, { useState, useEffect, useRef } from 'react';
import FooterPage from './FooterPage';
import { Star, Phone, Mail, MessageSquare, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import logo from '../images/logo.png';
import axios from "axios";
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserMeals, fetchUserMealPlans, selectUserMeals, selectUserMealPlans } from '../redux/userSlice';
import GreenWhiteBanner from './Homebanner';
import NavigationBar from './navigationHome';
import WhyChooseUs from '../components/why_choose_us';
import PopularMealPlans from '../components/popular_prodects';
import HowWeDeliver from '../components/how_we_deliver';
import CalorieCalculator from './Homepagesecondsection';
import EidOfferBanner from './EidOfferBanner';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

// Hero Images
import HeroNutrition from '../images/hero_nutrition_dark.png';
import HeroMealPrep from '../images/hero_meal_prep_containers.png';
import HeroDelivery from '../images/hero_delivery_box.png';
import HeroSuperfoods from '../images/hero_superfoods_closeup.png';
import HeroSmoothie from '../images/hero_smoothie_kitchen.png';

const DilyfitHomePage = () => {
  const navigate = useNavigate();
  const calculatorRef = useRef(null);

  const dispatch = useDispatch();
  const products = useSelector(selectUserMeals);
  const mealPlans = useSelector(selectUserMealPlans);

  // Hero slider
  const [currentSlide, setCurrentSlide] = useState(0);
  const backgroundImages = [HeroNutrition, HeroMealPrep, HeroDelivery, HeroSuperfoods, HeroSmoothie];

  // Toast state
  const [toast, setToast] = useState(null);

  // Eid banner state — show once per session
  const [showEidBanner, setShowEidBanner] = useState(
    () => sessionStorage.getItem('eidBannerDismissed') !== 'true'
  );

  // Contact form state
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', address: '', message: '',
  });

  // ─── Toast Helper ───────────────────────────────────────────────────────────
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ─── Eid Banner dismiss handler ─────────────────────────────────────────────
  const handleEidBannerClose = () => {
    setShowEidBanner(false);
    sessionStorage.setItem('eidBannerDismissed', 'true');
  };

  // ─── Fetch individual meals and meal plans via Redux ──────────────────────────
  useEffect(() => {
    dispatch(fetchUserMeals());
    dispatch(fetchUserMealPlans());
  }, [dispatch]);

  // ─── Hero Auto-Rotation — Fixed: functional update, no stale dependency ─────
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % backgroundImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [backgroundImages.length]);

  // ─── Navigation Handlers ─────────────────────────────────────────────────────
  const handleShopNowClick = () => navigate("/checkout");

  const handleLearnMoreClick = () => {
    calculatorRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // ─── Form Handlers ───────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = `New Inquiry from Daily Fit Website:\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nAddress: ${formData.address}\nMessage: ${formData.message}`;
    const encodedMessage = encodeURIComponent(message);
    // Fixed: UAE WhatsApp number
    const whatsappNumber = '+971562198110';
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
    setFormData({ name: '', email: '', phone: '', address: '', message: '' });
    // Fixed: toast instead of window.alert()
    showToast('Your inquiry has been sent via WhatsApp! ✅');
  };

  // ─── Reviews — Fixed: gradient initials instead of broken /api/placeholder ──
  const reviews = [
    {
      id: 1, name: 'John Doe', initials: 'JD',
      color: 'from-lime-500 to-green-600', rating: 5,
      text: 'The meal plans from DailyFit have completely transformed my fitness journey. Fresh ingredients and perfectly portioned — delivered right on time!',
      date: '15 Jan 2025'
    },
    {
      id: 2, name: 'Sarah Smith', initials: 'SS',
      color: 'from-blue-500 to-indigo-600', rating: 4,
      text: 'I love my nutrition plan. The customer service was excellent when I had questions about adjusting my macros for weight loss.',
      date: '28 Jan 2025'
    },
    {
      id: 3, name: 'Mike Johnson', initials: 'MJ',
      color: 'from-orange-500 to-red-600', rating: 5,
      text: "Best meal delivery service in Dubai. DailyFit has the perfect combination of quality, taste, and affordability. Highly recommend!",
      date: '10 Feb 2025'
    },
    {
      id: 4, name: 'Emily Chen', initials: 'EC',
      color: 'from-purple-500 to-pink-600', rating: 5,
      text: 'The delivery was always on time and the meals exceeded my expectations every single day. Will definitely keep my subscription!',
      date: '22 Feb 2025'
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* ── Eid Special Offer Notification ─ over hero section ─────────── */}
      {showEidBanner && <EidOfferBanner onClose={handleEidBannerClose} />}

      {/* ── Toast Notification (replaces window.alert) ────────────────────── */}
      <AnimatePresence>
        {toast && (
          <motion.div
            key="toast"
            initial={{ opacity: 0, y: -30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className={`fixed top-6 right-6 z-[200] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl text-white font-medium text-sm max-w-sm ${
              toast.type === 'success' ? 'bg-lime-600' : 'bg-red-600'
            }`}
          >
            <span className="text-lg">{toast.type === 'success' ? '✅' : '❌'}</span>
            <span>{toast.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Hero Carousel Section ─────────────────────────────────────────── */}
      <div className="relative h-screen xl:h-screen lg:max-h-[700px] max-h-[800px] overflow-hidden">

        {/* Navbar Layer */}
        <div className='absolute top-0 left-0 right-0 z-30 grid grid-cols-2'>
          <div className="flex z-20">
            <a href="/" className="flex">
              <img src={logo} alt="DailyFit Logo" className="h-60 w-80 object-contain" />
            </a>
          </div>
          {/* Desktop navigation only, mobile nav is sticky at bottom */}
          <div className="hidden md:block"><NavigationBar /></div>
        </div>

        {/* Background Slider Layer — Fixed: lazy load non-first images */}
        {backgroundImages.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              currentSlide === index ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img}
              alt={`Slide ${index + 1}`}
              className="w-full h-full object-cover"
              loading={index === 0 ? 'eager' : 'lazy'}
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
        ))}

        {/* Static Text Content Layer — Fixed: motion entrance animation */}
        <div className="relative z-20 h-full flex items-center mt-10 pointer-events-none">
          <div className="container mx-auto px-6 md:px-12 pointer-events-auto">
            <motion.div
              className="max-w-lg"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
                Fuel Your Fitness Journey
              </h1>
              <p className="text-xl text-white opacity-90 mb-8">
                Nutritionist-approved meals to fuel your workout routine
              </p>
              <div className="flex flex-wrap gap-4">
                <button
                  className="bg-lime-600 hover:bg-lime-700 text-white font-bold py-3 px-8 rounded-full transition duration-300 transform hover:scale-105 flex items-center"
                  onClick={handleShopNowClick}
                >
                  Explore Meals
                  <ArrowRight className="ml-2 h-5 w-5" />
                </button>
                {/* Fixed: "Learn More" now scrolls to calculator section */}
                <button
                  className="bg-transparent border-2 border-white text-white font-bold py-3 px-8 rounded-full hover:bg-white hover:text-lime-800 transition duration-300"
                  onClick={handleLearnMoreClick}
                >
                  Learn More
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hero Slide Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {backgroundImages.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`transition-all duration-300 rounded-full ${
                i === currentSlide ? 'w-6 h-2 bg-lime-400' : 'w-2 h-2 bg-white/50'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* WhatsApp Icon Button — Bottom Central-Right Side */}
        <motion.a
          href="https://wa.me/971562198110"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="absolute bottom-6 right-6 md:right-12 z-30 bg-[#25D366] hover:bg-[#20bd5a] text-white px-4 py-3 md:px-5 md:py-3 rounded-full flex items-center gap-2.5 shadow-xl hover:shadow-[0_10px_25px_rgba(37,211,102,0.4)] transition-all duration-300 transform hover:scale-105 active:scale-95 group"
          aria-label="Chat on WhatsApp"
        >
          <div className="relative">
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
            </span>
            <svg
              className="w-6 h-6 fill-current text-white flex-shrink-0"
              viewBox="0 0 24 24"
            >
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.105 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
            </svg>
          </div>
          <span className="font-bold text-sm hidden sm:inline tracking-wide">
            Chat on WhatsApp
          </span>
        </motion.a>
      </div>

      {/* ── Calorie Calculator & Featured Meals ───────────────────────────── */}
      {/* Fixed: ref for "Learn More" scroll target */}
      <div ref={calculatorRef} className="bg-white">
        {/* Fixed: mealPlansData passed — no more duplicate fetch inside component */}
        <CalorieCalculator products={products} mealPlansData={mealPlans} />
      </div>

      {/* ── Why Choose Us ─────────────────────────────────────────────────── */}
      <WhyChooseUs />

      {/* ── How We Deliver ────────────────────────────────────────────────── */}
      <HowWeDeliver />

      {/* ── Popular Meal Plans ────────────────────────────────────────────── */}
      {/* Fixed: mealPlansData passed — no more duplicate fetch inside component */}
      <PopularMealPlans mealPlansData={mealPlans} />

      {/* ── Choose Your Meal Plan (Banner) ────────────────────────────────── */}
      <div className="text-gray-900 py-10 w-full mx-auto text-center">
        <GreenWhiteBanner />
      </div>

      {/* ── Customer Reviews — Premium Redesign ───────────────────────────── */}
      <section className="py-24 relative overflow-hidden bg-white">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-[400px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse, rgba(132,204,22,0.06) 0%, transparent 70%)" }}
        />

        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 bg-lime-50 border border-lime-200 rounded-full px-4 py-1.5 text-lime-700 text-xs font-semibold uppercase tracking-widest mb-4">
              <Star size={12} className="fill-lime-700" />
              Success Stories
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight">
              What Our Customers Say
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Swiper
              modules={[Autoplay, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              breakpoints={{
                768: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              pagination={{ clickable: true, dynamicBullets: true }}
              className="pb-16 pt-4 px-4 -mx-4"
            >
              {reviews.map((review) => (
                <SwiperSlide key={review.id}>
                  <div className="bg-white rounded-3xl p-8 h-full border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(132,204,22,0.1)] transition-all duration-300 relative group flex flex-col">
                    {/* Decorative quote mark */}
                    <div className="absolute top-6 right-8 text-8xl font-serif leading-none text-lime-500/10 group-hover:text-lime-500/20 transition-colors pointer-events-none select-none">
                      "
                    </div>

                    <div className="flex items-center mb-6 relative z-10">
                      <div
                        className={`w-14 h-14 rounded-full bg-gradient-to-br ${review.color} flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-lg ring-4 ring-white`}
                      >
                        {review.initials}
                      </div>
                      <div className="ml-4">
                        <h4 className="font-bold text-gray-900 text-lg">{review.name}</h4>
                        <div className="flex mt-1 gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              className={i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200 fill-gray-200"}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 leading-relaxed text-base relative z-10 flex-grow">
                      "{review.text}"
                    </p>
                    <div className="mt-6 pt-6 border-t border-gray-50 text-sm text-gray-400 font-medium tracking-wide">
                      {review.date}
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>
        </div>
      </section>

      {/* ── Enquiry Form — Premium Redesign ───────────────────────────────── */}
      <section className="py-24 relative overflow-hidden" style={{ background: '#f8faf9' }}>
        {/* Background decorative shapes */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-lime-500/5 blur-3xl -z-10 rounded-full transform translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-1/4 h-1/2 bg-green-600/5 blur-3xl -z-10 rounded-full transform -translate-x-1/2"></div>

        <div className="container mx-auto px-6 max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-[2rem] shadow-2xl overflow-hidden border border-gray-100 flex flex-col lg:flex-row"
          >
            {/* Left Side: Contact Info (Dark Theme) */}
            <div
              className="lg:w-5/12 p-10 md:p-14 text-white relative overflow-hidden flex flex-col justify-between"
              style={{ background: 'linear-gradient(145deg, #166534 0%, #14532d 100%)' }}
            >
              {/* Decorative rings */}
              <div className="absolute -right-20 -top-20 w-64 h-64 border-[30px] border-white/5 rounded-full pointer-events-none" />
              <div className="absolute -left-10 -bottom-10 w-40 h-40 border-[20px] border-lime-400/10 rounded-full pointer-events-none" />

              <div className="relative z-10">
                <span className="inline-block px-3 py-1 bg-white/10 rounded-full text-lime-300 text-xs font-bold uppercase tracking-widest mb-6 border border-white/20">
                  Get in Touch
                </span>
                <h2 className="text-4xl font-extrabold mb-4">Contact Us</h2>
                <p className="text-lime-50/80 leading-relaxed mb-10 text-lg">
                  Ready to transform your health? Have questions about our plans? We're here to help you every step of the way.
                </p>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/10">
                      <Phone className="text-lime-300" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-lime-200/70 font-medium mb-1">Call Us</p>
                      <p className="font-semibold text-lg">04 565 4390</p>
                      <p className="text-white/80">+971 56 219 8110</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/10">
                      <MessageSquare className="text-lime-300" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-lime-200/70 font-medium mb-1">WhatsApp</p>
                      <p className="font-semibold text-lg">+971 56 219 8110</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0 border border-white/10">
                      <Mail className="text-lime-300" size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-lime-200/70 font-medium mb-1">Email</p>
                      <p className="font-semibold text-lg">support@dailyfit.ae</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side: Contact Form (Light Theme) */}
            <div className="lg:w-7/12 p-10 md:p-14 bg-white relative">
              <h3 className="text-2xl md:text-3xl font-extrabold mb-8 text-gray-900">
                Send us a message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="name" className="text-sm font-bold text-gray-700 ml-1">Full Name *</label>
                    <input
                      type="text" id="name" name="name" value={formData.name} onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500/50 focus:border-lime-500 focus:bg-white transition-all duration-200"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-bold text-gray-700 ml-1">Email Address *</label>
                    <input
                      type="email" id="email" name="email" value={formData.email} onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500/50 focus:border-lime-500 focus:bg-white transition-all duration-200"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="phone" className="text-sm font-bold text-gray-700 ml-1">WhatsApp Number *</label>
                    <input
                      type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange}
                      placeholder="+971 50 000 0000"
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500/50 focus:border-lime-500 focus:bg-white transition-all duration-200"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="address" className="text-sm font-bold text-gray-700 ml-1">Delivery Area (Optional)</label>
                    <input
                      type="text" id="address" name="address" value={formData.address} onChange={handleChange}
                      placeholder="e.g. Dubai Marina"
                      className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500/50 focus:border-lime-500 focus:bg-white transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm font-bold text-gray-700 ml-1">How can we help? *</label>
                  <textarea
                    id="message" name="message" rows="4" value={formData.message} onChange={handleChange}
                    placeholder="Tell us about your fitness goals..."
                    className="w-full px-5 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-lime-500/50 focus:border-lime-500 focus:bg-white transition-all duration-200 resize-none"
                    required
                  ></textarea>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full py-4 rounded-xl font-bold text-white text-lg flex items-center justify-center gap-3 shadow-[0_10px_20px_rgba(22,163,74,0.2)] hover:shadow-[0_15px_30px_rgba(22,163,74,0.3)] transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg, #16a34a 0%, #84cc16 100%)' }}
                >
                  <MessageSquare size={20} />
                  Send Message via WhatsApp
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <FooterPage />

      {/* ── Sticky mobile navigation bar ─────────────────────────────────── */}
      <div className="fixed bottom-0 left-0 w-full z-50 md:hidden">
        <NavigationBar />
      </div>

    </div>
  );
};

export default DilyfitHomePage;